import React, { useState } from 'react';
import { Card, Button, Alert } from './UIComponents';
import { publishBacklogToAzure, PublishAzureStoryPayload } from '../services/api';
import { parseBacklogMarkdown } from '../utils/backlogParser';
import { ParsedBacklog } from '../types/story';

interface AzureDevOpsPublisherProps {
  backlog: string | null;
  selectedConfigId: string;
  parsedBacklog?: ParsedBacklog | null;
}

export const AzureDevOpsPublisher: React.FC<AzureDevOpsPublisherProps> = ({
  backlog,
  selectedConfigId,
  parsedBacklog
}) => {
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  const handlePublish = async () => {
    if (!backlog) {
      setAlert({
        type: 'warning',
        message: 'No hay backlog generado para publicar'
      });
      return;
    }

    if (!selectedConfigId) {
      setAlert({
        type: 'warning',
        message: 'Selecciona una configuración de Azure DevOps primero'
      });
      return;
    }

    if (!window.confirm(
      '¿Estás seguro de publicar este backlog en Azure DevOps Boards?\n\n' +
      'Se crearán épicas e historias de usuario en el proyecto configurado.'
    )) {
      return;
    }

    setPublishing(true);
    setResult(null);
    setAlert(null);

    try {
      let structuredStories: PublishAzureStoryPayload[] = [];
      if (parsedBacklog?.stories?.length) {
        structuredStories = (parsedBacklog.stories || []).map((story) => ({
          id: story.id,
          title: story.title,
          user_story: story.user_story,
          rol: story.rol,
          criterios_funcionales: story.criterios_funcionales || [],
          criterios_tecnicos: story.criterios_tecnicos || [],
          figma_links: story.figma_links || [],
          insumos: story.insumos || [],
          story_points: story.story_points,
          priority: story.priority,
          priority_sequence: story.priority_sequence ?? null,
          epic: story.epic,
          status: story.status,
        }));
      } else {
        try {
          const parsedFallback = parseBacklogMarkdown(backlog);
          structuredStories = (parsedFallback.stories || []).map((story) => ({
            id: story.id,
            title: story.title,
            user_story: story.user_story,
            rol: story.rol,
            criterios_funcionales: story.criterios_funcionales || [],
            criterios_tecnicos: story.criterios_tecnicos || [],
            figma_links: story.figma_links || [],
            insumos: story.insumos || [],
            story_points: story.story_points,
            priority: story.priority,
            priority_sequence: story.priority_sequence ?? null,
            epic: story.epic,
            status: story.status,
          }));
        } catch (parseError) {
          console.warn('[AzurePublisher] Error parsing structured backlog, using markdown fallback:', parseError);
        }
      }

      const response = await publishBacklogToAzure(selectedConfigId, backlog, structuredStories);
      
      if (response.success) {
        setResult(response);
        setAlert({
          type: 'success',
          message: `✓ Backlog publicado exitosamente: ${response.epics_created} épicas, ${response.stories_created} historias de usuario, ${response.links_created} enlaces creados`
        });
      } else {
        setAlert({
          type: 'error',
          message: `Error al publicar: ${response.error}`
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Error al publicar backlog'
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gft-dark flex items-center">
          <span className="mr-3">🚀</span>
          Publicar en Azure DevOps
        </h3>
        <Button
          onClick={handlePublish}
          loading={publishing}
          disabled={!backlog || !selectedConfigId || publishing}
          variant="primary"
        >
          {publishing ? 'Publicando...' : '📤 Integrar con Azure DevOps Boards'}
        </Button>
      </div>

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {!backlog && (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-yellow-800 text-sm">
            ⚠️ Genera un backlog primero antes de publicar en Azure DevOps
          </p>
        </div>
      )}

      {!selectedConfigId && backlog && (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-yellow-800 text-sm">
            ⚠️ Selecciona una configuración de Azure DevOps para continuar
          </p>
        </div>
      )}

      {result && result.success && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 font-semibold">Épicas Creadas</p>
              <p className="text-3xl font-bold text-green-800">{result.epics_created}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 font-semibold">Historias Creadas</p>
              <p className="text-3xl font-bold text-blue-800">{result.stories_created}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700 font-semibold">Tareas Creadas</p>
              <p className="text-3xl font-bold text-amber-800">{result.tasks_created || 0}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700 font-semibold">Enlaces Creados</p>
              <p className="text-3xl font-bold text-purple-800">{result.links_created}</p>
            </div>
          </div>

          {/* Work Items Created */}
          {result.items && result.items.length > 0 && (
            <div className="max-h-[500px] overflow-y-auto">
              <h4 className="font-bold text-gray-900 mb-3">Work Items Creados:</h4>
              <div className="space-y-3">
                {result.items.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      item.type === 'Epic'
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {item.type === 'Epic' ? '📋' : '📝'} {item.title}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">ID:</span> {item.id}
                          {item.parent_epic && (
                            <span className="ml-2">
                              <span className="font-semibold">Épica:</span> {item.parent_epic}
                            </span>
                          )}
                        </p>
                        
                        {/* Show tasks if present */}
                        {item.tasks && item.tasks.length > 0 && (
                          <div className="mt-2 ml-4 border-l-2 border-amber-300 pl-3">
                            <p className="text-xs font-semibold text-amber-700 mb-1">
                              📋 Tareas ({item.tasks.length}):
                            </p>
                            <ul className="space-y-1">
                              {item.tasks.map((task: any, taskIndex: number) => (
                                <li key={taskIndex} className="flex items-center text-xs text-gray-600">
                                  <span className="w-2 h-2 bg-amber-400 rounded-full mr-2"></span>
                                  <span className="flex-1">{task.title}</span>
                                  <a
                                    href={task.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-amber-600 hover:text-amber-800"
                                  >
                                    #{task.id}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
                      >
                        Ver en Azure →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {result.errors && result.errors.length > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-800 mb-2">⚠️ Errores Durante la Publicación:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {result.errors.map((error: string, index: number) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Info Card */}
      <div className="p-4 bg-gradient-to-br from-gft-purple to-gft-dark text-white rounded-lg">
        <h4 className="font-bold text-lg mb-2">📖 Proceso de Publicación</h4>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start">
            <span className="mr-2">1️⃣</span>
            <span>Analiza el backlog generado para identificar épicas e historias</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">2️⃣</span>
            <span>Crea épicas como work items de tipo "Epic"</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">3️⃣</span>
            <span>Crea historias con formato HTML: <b>"Como [rol], quiero [acción], para [beneficio]"</b></span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">4️⃣</span>
            <span>Crea <b>4 tareas estándar</b> para cada HU y las vincula automáticamente</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">5️⃣</span>
            <span>Enlaza historias con épicas y aplica área/iteración según configuración</span>
          </li>
        </ul>
        <div className="mt-3 p-2 bg-white/10 rounded text-xs">
          <p className="font-semibold">📋 Tareas automáticas por HU:</p>
          <ul className="mt-1 space-y-1">
            <li>• 1-Contextualización/entendimiento</li>
            <li>• 2-Desarrollo</li>
            <li>• 3-Pruebas unitarias</li>
            <li>• 4-Despliegue en ambientes preproductivos</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};
